import React, { useEffect, useMemo, useState } from 'react';
import {
  Bitcoin,
  Calculator,
  Coins,
  Info,
  LineChart,
  RotateCcw,
  Sparkles,
  TrendingUp,
  X,
  Zap,
} from 'lucide-react';
import { getThemeColor } from '../../config/appConfig';
import apiService from '../../utils/apiService';

const THEME = getThemeColor('primary');

const ICON_BY_KEY = {
  fx: TrendingUp,
  ultra: Zap,
  zero: LineChart,
  cfd: LineChart,
  crypto: Coins,
  btc: Bitcoin,
  tndc: Sparkles,
  undc: Coins,
};

const resolveRuleMeta = (pointName = '') => {
  const n = String(pointName).toLowerCase();
  if (n.includes('ultra')) return { key: 'ultra', Icon: ICON_BY_KEY.ultra };
  if (n.includes('zero')) return { key: 'zero', Icon: ICON_BY_KEY.zero };
  if (n.includes('cfd')) return { key: 'cfd', Icon: ICON_BY_KEY.cfd };
  if (n.includes('crypto')) return { key: 'crypto', Icon: ICON_BY_KEY.crypto };
  if (n.includes('btc')) return { key: 'btc', Icon: ICON_BY_KEY.btc };
  if (n.includes('tndc')) return { key: 'tndc', Icon: ICON_BY_KEY.tndc };
  if (n.includes('undc')) return { key: 'undc', Icon: ICON_BY_KEY.undc };
  if (n.includes('fx')) return { key: 'fx', Icon: ICON_BY_KEY.fx };
  return { key: 'other', Icon: Calculator };
};

export const formatRateLabel = (pointValue) => {
  const rate = Number(pointValue);
  if (!Number.isFinite(rate)) return '1 = 0 pts';
  if (Number.isInteger(rate)) return `1 = ${rate} pt${rate === 1 ? '' : 's'}`;
  return `1 = ${Number(rate.toFixed(4))} pts`;
};

const formatPoints = (n) => {
  if (!Number.isFinite(n)) return '0';
  if (Number.isInteger(n)) return n.toLocaleString('en-US');
  return Number(n.toFixed(2)).toLocaleString('en-US');
};

export const normalizePointsCalculatorRules = (payload) => {
  const list = Array.isArray(payload) ? payload : [];
  return list
    .map((item, index) => {
      const id =
        item.affiliatePointsCalculatorId ??
        item.AffiliatePointsCalculatorId ??
        item.id ??
        index + 1;
      const name = item.pointName || item.PointName || `Instrument ${index + 1}`;
      const rate = Number(item.pointValue ?? item.PointValue);
      const meta = resolveRuleMeta(name);
      return {
        id: String(id),
        name,
        rate: Number.isFinite(rate) ? rate : 0,
        rateLabel: formatRateLabel(rate),
        hint: `${name} converts at ${formatRateLabel(rate).replace('1 = ', '')} per unit of volume.`,
        Icon: meta.Icon,
        sortKey: meta.key,
        createdDateTime: item.createdDateTime || item.CreatedDateTime || null,
        raw: item,
      };
    })
    .filter((rule) => rule.name);
};

const emptyValues = (rules) =>
  rules.reduce((acc, rule) => {
    acc[rule.id] = '';
    return acc;
  }, {});

const PointsCalculatorModal = ({ open, onClose }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [values, setValues] = useState({});
  const [tipId, setTipId] = useState(null);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setTipId(null);
    setError('');
    setLoading(true);
    setValues({});
    setRules([]);

    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';

    (async () => {
      const res = await apiService.getPointsCalculatorList();
      if (cancelled) return;
      if (!res.success) {
        setError(res.error || 'Failed to load points calculator');
        setRules([]);
        setValues({});
        setLoading(false);
        return;
      }
      const nextRules = normalizePointsCalculatorRules(res.data);
      setRules(nextRules);
      setValues(emptyValues(nextRules));
      setLoading(false);
    })();

    return () => {
      cancelled = true;
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const contributions = useMemo(
    () =>
      rules.map((rule) => {
        const volume = Number(values[rule.id]) || 0;
        return { id: rule.id, qty: volume, points: volume * rule.rate };
      }),
    [rules, values]
  );

  const total = useMemo(
    () => contributions.reduce((sum, row) => sum + row.points, 0),
    [contributions]
  );

  const activeCount = contributions.filter((row) => row.qty > 0).length;

  if (!open) return null;

  return (
    <div className="at-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="at-modal at-modal--calc"
        role="dialog"
        aria-modal="true"
        aria-labelledby="at-calc-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="at-modal__head at-modal__head--calc">
          <div className="at-modal__brand">
            <div className="at-modal__brand-icon" style={{ background: THEME }}>
              <Calculator className="w-5 h-5" />
            </div>
            <div>
              <h3 id="at-calc-title">Points Calculator</h3>
              <p>Enter client activity volume to estimate your level points instantly.</p>
            </div>
          </div>
          <button type="button" className="at-modal__close" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="at-calc">
          <div className="at-calc__panel">
            <div className="at-calc__panel-head">
              <span>Points map</span>
              <span>{loading ? '…' : `${rules.length} instruments`}</span>
            </div>

            {loading ? (
              <p className="at-calc__summary-empty" style={{ padding: '1.25rem 0.25rem' }}>
                Loading calculator rules…
              </p>
            ) : error ? (
              <p className="at-calc__summary-empty" style={{ padding: '1.25rem 0.25rem', color: '#b91c1c' }}>
                {error}
              </p>
            ) : rules.length === 0 ? (
              <p className="at-calc__summary-empty" style={{ padding: '1.25rem 0.25rem' }}>
                No calculator instruments available.
              </p>
            ) : (
              <div className="at-calc__grid">
                {rules.map((rule) => {
                  const Icon = rule.Icon;
                  const volume = Number(values[rule.id]) || 0;
                  const pts = volume * rule.rate;
                  const filled = values[rule.id] !== '' && volume > 0;

                  return (
                    <label
                      key={rule.id}
                      className={`at-calc__field${filled ? ' is-filled' : ''}`}
                    >
                      <div className="at-calc__field-top">
                        <span className="at-calc__field-icon">
                          <Icon className="w-3.5 h-3.5" />
                        </span>
                        <span className="at-calc__field-name">{rule.name}</span>
                        <button
                          type="button"
                          className="at-calc__info"
                          title={rule.hint}
                          aria-label={rule.hint}
                          onClick={(e) => {
                            e.preventDefault();
                            setTipId((prev) => (prev === rule.id ? null : rule.id));
                          }}
                        >
                          <Info className="w-3.5 h-3.5" />
                        </button>
                        <span className="at-calc__rate">{rule.rateLabel}</span>
                      </div>
                      {tipId === rule.id ? <p className="at-calc__tip">{rule.hint}</p> : null}
                      <div className="at-calc__input-wrap">
                        <input
                          type="number"
                          min="0"
                          step="any"
                          placeholder="0"
                          value={values[rule.id] ?? ''}
                          onChange={(e) =>
                            setValues((prev) => ({
                              ...prev,
                              [rule.id]: e.target.value,
                            }))
                          }
                        />
                      </div>
                      {filled ? (
                        <div className="at-calc__earn">
                          Earns <strong>{formatPoints(pts)}</strong> pts
                        </div>
                      ) : null}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <aside className="at-calc__side">
            <div className="at-calc__total" style={{ background: `linear-gradient(160deg, ${THEME}, #5c0000)` }}>
              <span className="at-calc__total-label">Total Points</span>
              <strong className="at-calc__total-value">{formatPoints(total)}</strong>
              <span className="at-calc__total-meta">
                {activeCount > 0
                  ? `${activeCount} active input${activeCount === 1 ? '' : 's'}`
                  : 'Add volumes to calculate'}
              </span>
            </div>

            <div className="at-calc__summary">
              <div className="at-calc__summary-title">Breakdown</div>
              {activeCount === 0 ? (
                <p className="at-calc__summary-empty">No activity entered yet.</p>
              ) : (
                <ul className="at-calc__summary-list">
                  {contributions
                    .filter((row) => row.qty > 0)
                    .map((row) => {
                      const rule = rules.find((r) => r.id === row.id);
                      return (
                        <li key={row.id}>
                          <span>{rule?.name}</span>
                          <strong>{formatPoints(row.points)}</strong>
                        </li>
                      );
                    })}
                </ul>
              )}
            </div>

            <button
              type="button"
              className="at-calc__reset"
              disabled={loading || rules.length === 0}
              onClick={() => {
                setValues(emptyValues(rules));
                setTipId(null);
              }}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset all
            </button>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default PointsCalculatorModal;
